/**
 * Nevi recommendations tab — gate chips + view-only desk.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Candidate, getOptionLabel, getOptionIcon } from '../../lib/api';
import { fmt } from '../../lib/format';
import { copy } from './brand';
import { CandidateGateRow } from './ui/GateChips';
import { PullToRefresh } from './ui/PullToRefresh';
import { Skeleton } from './ui/Skeleton';
import { MobNum, MobMetric } from './ui/MobNum';

function isPassed(c: Candidate): boolean {
  return c.reject_reason == null || String(c.reject_reason).trim() === '';
}

export function MobileCandidatesPage() {
  const [tab, setTab] = useState<'passed' | 'rejected'>('passed');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['candidates', 'latest-cycle'],
    queryFn: () =>
      api.get<{
        candidates: Candidate[];
        count: number;
        cycle_id?: string | null;
        stats?: { total: number; passed: number; rejected: number };
      }>('/api/bot/candidates?limit=400&cycle_id=latest'),
    refetchInterval: 30_000,
  });

  const candidates = data?.candidates || [];
  const stats = data?.stats;
  const passed = candidates.filter(isPassed);
  const rejected = candidates.filter((c) => !isPassed(c));
  const passedSorted = [...passed].sort((a, b) => Number(a.soft_score) - Number(b.soft_score));
  const displayList = tab === 'passed' ? passedSorted : rejected;
  const total = stats?.total ?? candidates.length;

  return (
    <div className="tc-mob-page tc-mob-page--ht tc-mob-page--eg">
      <PullToRefresh onRefresh={() => qc.invalidateQueries({ queryKey: ['candidates'] })}>
        <div className="tc-mob-page__header tc-mob-page__header--eg">
          <div className="tc-mob-page__title">{copy.nevi.title}</div>
          <div className="tc-mob-page__sub">
            {total} dianalisis · {copy.nevi.sub}
          </div>
        </div>

        <div className={`tc-mob-tab-row tc-mob-tab-row--tint-${tab}`}>
          <button
            type="button"
            className={`tc-mob-tab ${tab === 'passed' ? 'tc-mob-tab--active' : ''}`}
            onClick={() => {
              setTab('passed');
              if (navigator.vibrate) navigator.vibrate(6);
            }}
          >
            {copy.nevi.tabPass} ({passed.length})
          </button>
          <button
            type="button"
            className={`tc-mob-tab ${tab === 'rejected' ? 'tc-mob-tab--active' : ''}`}
            onClick={() => {
              setTab('rejected');
              if (navigator.vibrate) navigator.vibrate(6);
            }}
          >
            {copy.nevi.tabReject} ({rejected.length})
          </button>
        </div>

        {isLoading ? (
          <Skeleton rows={5} />
        ) : displayList.length === 0 ? (
          <div className="tc-mob-empty">
            {tab === 'passed' ? copy.nevi.emptyPass : copy.nevi.emptyReject}
          </div>
        ) : (
          <div className="tc-mob-card-list">
            {displayList.slice(0, 50).map((c) => {
              const expanded = expandedId === c.id;
              return (
                <div
                  key={c.id}
                  className={`tc-mob-card tc-mob-card--ht tc-mob-card--eg tc-mob-card--expandable ${
                    expanded ? 'tc-mob-card--expanded' : ''
                  }`}
                  onClick={() => setExpandedId(expanded ? null : c.id)}
                >
                  <div className="tc-mob-card__header">
                    <span
                      className={`tc-mob-badge tc-mob-badge--${
                        c.option_type === 'PUT' ? 'cyan' : 'amber'
                      }`}
                    >
                      {getOptionIcon(c.option_type)} {c.option_type}
                    </span>
                    <span className="tc-mob-card__symbol">{c.underlying}</span>
                    <MobNum
                      className="tc-mob-card__strike"
                      size="sm"
                      tone="muted"
                      full={fmt.number(c.strike_price, 0)}
                    >
                      {fmt.compact(c.strike_price, 2)}
                    </MobNum>
                    <MobNum className="tc-mob-card__score" size="sm" full={fmt.score(c.soft_score)}>
                      {fmt.score(c.soft_score)}
                    </MobNum>
                  </div>

                  <div className="tc-mob-card__body tc-mob-card__body--metrics">
                    <MobMetric
                      label="APR"
                      value={fmt.percent(c.apr, 2)}
                      full={fmt.percent(c.apr, 4)}
                      tone="green"
                    />
                    <MobMetric label="Settle" value={fmt.countdown(c.settle_date)} />
                  </div>

                  <CandidateGateRow
                    gates={{
                      macro: c.gate_passed.macro,
                      liquidation_zone: c.gate_passed.liquidation_zone,
                      min_distance: c.gate_passed.min_distance,
                      soft_score_ok: !String(c.reject_reason || '').startsWith('soft_score'),
                    }}
                  />

                  {expanded && (
                    <div className="tc-mob-card__detail">
                      <div className="tc-mob-card__detail-row">
                        <span>Jenis</span>
                        <span>{getOptionLabel(c.option_type)}</span>
                      </div>
                      <div className="tc-mob-card__detail-row">
                        <span>Strike</span>
                        <MobNum size="sm">{fmt.number(c.strike_price, 0)}</MobNum>
                      </div>
                      <div className="tc-mob-card__detail-row">
                        <span>Settle</span>
                        <span>{fmt.dateDay(c.settle_date)}</span>
                      </div>
                      <div className="tc-mob-card__detail-row">
                        <span>Days</span>
                        <span>{c.days_to_settle} hari</span>
                      </div>
                      {c.reject_reason && (
                        <div className="tc-mob-card__detail-row tc-mob-card__detail-row--danger">
                          <span>Alasan</span>
                          <span>{c.reject_reason}</span>
                        </div>
                      )}
                      {c.narrative && (
                        <div className="tc-mob-card__narrative">
                          <strong>Nevi notes · </strong>
                          {c.narrative.slice(0, 220)}
                          {c.narrative.length > 220 ? '…' : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}
